// Scenario engine skeleton (spec 11 §Planning "Scenarios").
//
// A scenario is a base period plus a list of changes (`add_seat`, `remove_seat`,
// `change_holder`, `change_budget`). The engine applies them in order to a baseline and returns
// cost, expected KPI impact and margin. Money is integer minor units per period; KPI values stay
// numbers (they are not money) and attainment comes from `@companyos/domain-kpi`.
//
// Skeleton scope: revenue is held at the baseline (no revenue model yet), a seat's cost is a
// flat per-period amount, and a KPI delta is additive per seat. "Hire a human vs a DE for this
// seat" is two scenarios with `change_holder` and different `assumedCostMinor`/deltas.

import { attainment, type TargetDirection } from "@companyos/domain-kpi";
import { div, type Rational, rat } from "./rational";

export type HolderKind = "person" | "digital_employee";

export interface KpiDelta {
  metricKey: string;
  /** Change in the metric's period value per seat. */
  delta: number;
}

export type ScenarioChange =
  | {
      op: "add_seat";
      seatTemplate: string;
      holderKind: HolderKind;
      count: number;
      assumedCostMinor: bigint;
      assumedKpiDelta?: readonly KpiDelta[];
    }
  | {
      op: "remove_seat";
      seatTemplate: string;
      holderKind: HolderKind;
      count: number;
      /** The KPI contribution each removed seat took with it (subtracted). */
      assumedKpiDelta?: readonly KpiDelta[];
    }
  | {
      op: "change_holder";
      seatTemplate: string;
      fromHolderKind: HolderKind;
      holderKind: HolderKind;
      count: number;
      assumedCostMinor: bigint;
      assumedKpiDelta?: readonly KpiDelta[];
    }
  | { op: "change_budget"; nodeId: string; amountMinor: bigint };

export interface SeatLine {
  seatTemplate: string;
  holderKind: HolderKind;
  count: number;
  costPerSeatMinor: bigint;
}

export interface KpiLine {
  metricKey: string;
  actual: number;
  target: number;
  direction: TargetDirection;
  /** Upper bound for `between`. */
  max?: number | null;
  /** Attainment cap (KPI Beast default 1). */
  cap?: number;
}

export interface ScenarioBaseline {
  basePeriodKey: string;
  revenueMinor: bigint;
  /** Costs that no seat line explains (overhead, software); carried unchanged. */
  otherCostMinor: bigint;
  seats: readonly SeatLine[];
  budgets: Readonly<Record<string, bigint>>;
  kpis: readonly KpiLine[];
}

export interface KpiImpact {
  metricKey: string;
  baseActual: number;
  scenarioActual: number;
  baseAttainment: number;
  scenarioAttainment: number;
}

export interface ScenarioResult {
  basePeriodKey: string;
  cost: { baseMinor: bigint; scenarioMinor: bigint; deltaMinor: bigint };
  revenueMinor: bigint;
  margin: { baseMinor: bigint; scenarioMinor: bigint; scenarioRatio: Rational | null };
  seats: SeatLine[];
  budgets: Record<string, bigint>;
  kpis: KpiImpact[];
}

export class ScenarioError extends Error {
  constructor(
    public readonly index: number,
    message: string,
  ) {
    super(`change ${index}: ${message}`);
    this.name = "ScenarioError";
  }
}

function seatCost(seats: readonly SeatLine[]): bigint {
  return seats.reduce((sum, s) => sum + BigInt(s.count) * s.costPerSeatMinor, 0n);
}

/** Take `count` seats out of the lines for (template, kind), most expensive line last. */
function takeSeats(
  seats: SeatLine[],
  template: string,
  kind: HolderKind,
  count: number,
  index: number,
): SeatLine[] {
  const available = seats
    .filter((s) => s.seatTemplate === template && s.holderKind === kind)
    .reduce((n, s) => n + s.count, 0);
  if (available < count)
    throw new ScenarioError(index, `only ${available} ${kind} seat(s) of ${template} to remove`);
  let left = count;
  return seats.map((s) => {
    if (left === 0 || s.seatTemplate !== template || s.holderKind !== kind) return s;
    const taken = Math.min(left, s.count);
    left -= taken;
    return { ...s, count: s.count - taken };
  });
}

export function runScenario(
  baseline: ScenarioBaseline,
  changes: readonly ScenarioChange[],
): ScenarioResult {
  let seats: SeatLine[] = baseline.seats.map((s) => ({ ...s }));
  const budgets: Record<string, bigint> = { ...baseline.budgets };
  const kpiDelta = new Map<string, number>();
  const applyKpi = (deltas: readonly KpiDelta[] | undefined, count: number, sign: 1 | -1) => {
    for (const d of deltas ?? [])
      kpiDelta.set(d.metricKey, (kpiDelta.get(d.metricKey) ?? 0) + sign * count * d.delta);
  };

  changes.forEach((c, i) => {
    if (c.op !== "change_budget" && (!Number.isInteger(c.count) || c.count <= 0))
      throw new ScenarioError(i, "count must be a positive integer");
    if ("assumedCostMinor" in c && c.assumedCostMinor < 0n)
      throw new ScenarioError(i, "assumed cost cannot be negative");
    switch (c.op) {
      case "add_seat":
        seats.push({
          seatTemplate: c.seatTemplate,
          holderKind: c.holderKind,
          count: c.count,
          costPerSeatMinor: c.assumedCostMinor,
        });
        applyKpi(c.assumedKpiDelta, c.count, 1);
        break;
      case "remove_seat":
        seats = takeSeats(seats, c.seatTemplate, c.holderKind, c.count, i);
        applyKpi(c.assumedKpiDelta, c.count, -1);
        break;
      case "change_holder":
        if (c.fromHolderKind === c.holderKind)
          throw new ScenarioError(i, "change_holder needs two different holder kinds");
        seats = takeSeats(seats, c.seatTemplate, c.fromHolderKind, c.count, i);
        seats.push({
          seatTemplate: c.seatTemplate,
          holderKind: c.holderKind,
          count: c.count,
          costPerSeatMinor: c.assumedCostMinor,
        });
        applyKpi(c.assumedKpiDelta, c.count, 1);
        break;
      case "change_budget":
        if (c.amountMinor < 0n) throw new ScenarioError(i, "budget cannot be negative");
        budgets[c.nodeId] = c.amountMinor;
        break;
    }
  });
  seats = seats.filter((s) => s.count > 0);

  const baseCost = seatCost(baseline.seats) + baseline.otherCostMinor;
  const scenarioCost = seatCost(seats) + baseline.otherCostMinor;
  const scenarioMargin = baseline.revenueMinor - scenarioCost;
  const kpis = baseline.kpis.map((k) => {
    const scenarioActual = k.actual + (kpiDelta.get(k.metricKey) ?? 0);
    const score = (actual: number) =>
      attainment(k.direction, actual, k.target, k.cap ?? 1, k.max ?? null);
    return {
      metricKey: k.metricKey,
      baseActual: k.actual,
      scenarioActual,
      baseAttainment: score(k.actual),
      scenarioAttainment: score(scenarioActual),
    };
  });
  return {
    basePeriodKey: baseline.basePeriodKey,
    cost: { baseMinor: baseCost, scenarioMinor: scenarioCost, deltaMinor: scenarioCost - baseCost },
    revenueMinor: baseline.revenueMinor,
    margin: {
      baseMinor: baseline.revenueMinor - baseCost,
      scenarioMinor: scenarioMargin,
      scenarioRatio:
        baseline.revenueMinor === 0n ? null : div(rat(scenarioMargin), rat(baseline.revenueMinor)),
    },
    seats,
    budgets,
    kpis,
  };
}
